import { Component, EventEmitter, Input, Output } from '@angular/core';
import { StringModifierServiceProvider } from '@services/helpers/string-modifier-service/stringModifier.service';
import { InvoicesDataProvider } from '@services/invoice/invoices-data-service/invoices.data.service';
import { PaymentsServiceProvider } from '@services/payments/payments-service/payments.service';
import { QuoteUpdateService } from '@services/quote/quote-update.service';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { OrderByNamePipe } from '@pipes/order-by/order-by-name';
import { PaymentsDataServiceProvider } from '@services/payments/payments-data-service/payments.data.service';
import { Summary } from '@models/models';

@Component({
	selector: 'payment-schedule',
	template: require('./payment-schedule.html'),
	styles: [require('./payment-schedule.scss')]
})
export class PaymentScheduleComponent {
	@Input('paymentScheduleNames') paymentScheduleNames;
	@Input('paymentScheduleTemplateId') paymentScheduleTemplateId;
	@Input('payments') payments;
	@Input('issueDate') issueDate;
	@Input('jobStartDate') jobStartDate;
	@Input('currencySymbol') currencySymbol;
	@Input('datePlaceHolder') datePlaceHolder;
	@Input('job') job;
	@Input('quoteTotal') quoteTotal: number;
	@Input('isFixed') isFixed: boolean;
	@Input('paymentsFromServer') paymentsFromServer;
	@Input('paymentScheduleIdFromServer') paymentScheduleIdFromServer: number;
	@Input('isInvoice') isInvoice: boolean;
	@Input('summary') summary: Summary;
	@Output() paymentScheduleChange = new EventEmitter();
	isPartlyPaid: boolean;
	chosenPaymentSchedule;
	templateFromServer;
	paymentsToShow = [];
	now = this.quoteUpdateService.getNowTextFormat();
	disablePaymentSchedule = false;
	shouldGetDefaultPayments = true;

	get paymentSchedule() {
		return this.chosenPaymentSchedule;
	}

	@Input('paymentSchedule')
	set paymentSchedule(value) {
		this.chosenPaymentSchedule = value;
		if (value) {
			this.templateFromServer = this.job.paymentScheduleId;
			this.sortScheduleByAphabet();
			this.addSelectedSchedule(value);
		}
	}

	ngAfterViewInit(): void {
		if (this.paymentScheduleTemplateId === -1) {
			this.getPaymentScheduleNew(-1, true);
		} else {
			this.getPaymentScheduleNew(0, false);
		}
		this.disablePaymentSchedule = this.quoteTotal === 0;
		this.isPartlyPaid =
			this.paymentService.isPaidPayments(this.payments) ||
			this.paymentService.isWrittenOffPayments(this.payments);
	}

	constructor(
		private quoteUpdateService: QuoteUpdateService,
		private invoicesDataService: InvoicesDataProvider,
		private paymentService: PaymentsServiceProvider,
		private stringModifierService: StringModifierServiceProvider,
		private timeService: TimeServiceProvider,
		private paymentsDataService: PaymentsDataServiceProvider,
		private orderByName: OrderByNamePipe
	) {}

	sortScheduleByAphabet() {
		this.paymentScheduleNames = this.orderByName.transform(
			this.paymentScheduleNames
		);
	}

	addSelectedSchedule(schedule): void {
		schedule.name = this.stringModifierService.addSelectedText(schedule.name);
		schedule.id = 0;
		this.paymentScheduleNames.push(schedule);
		this.paymentScheduleTemplateId = schedule.id;
	}

	emitPaymentsChanges(): void {
		this.payments = this.paymentsToShow;
		this.paymentScheduleChange.emit({
			id: this.paymentScheduleTemplateId,
			payments: this.paymentsToShow
		});
	}

	getPaymentScheduleNew(
		paymentScheduleId: number,
		needToSendPayments: boolean,
		quoteTotal?: number,
		refreshAmount?: boolean
	) {
		if (quoteTotal != null) {
			this.quoteTotal = quoteTotal;
			if (quoteTotal === 0) {
				this.hideAndResetPaymentSchedule();
			} else {
				this.disablePaymentSchedule = false;
			}
		}
		if (
			(needToSendPayments &&
				+paymentScheduleId === 0 &&
				this.shouldGetDefaultPayments) ||
			(refreshAmount &&
				+paymentScheduleId === 0 &&
				this.shouldGetDefaultPayments)
		) {
			paymentScheduleId = +this.paymentScheduleIdFromServer;
			this.payments = this.formPaymentsToShow(
				JSON.parse(JSON.stringify(this.paymentsFromServer))
			);
			needToSendPayments = true;
		}
		if (+paymentScheduleId === 0 && this.shouldGetDefaultPayments) {
			this.paymentsToShow = this.formPaymentsToShow(
				JSON.parse(JSON.stringify(this.paymentsFromServer))
			);
			this.emitPaymentsChanges();
		} else {
			const paymentScheduleObject = { id: +paymentScheduleId };
			this.formPaymentsNew(paymentScheduleObject, needToSendPayments);
		}
		if (!this.shouldGetDefaultPayments) {
			this.shouldGetDefaultPayments = true;
		}
	}

	hideAndResetPaymentSchedule() {
		this.disablePaymentSchedule = true;
		this.paymentScheduleTemplateId = -1;
	}

	formPaymentsNew(paymentSchedule, needToSendPayments: boolean) {
		if (
			paymentSchedule.id === 0 ||
			paymentSchedule.id === this.templateFromServer
		) {
			this.job.paymentScheduleId = 0;
		} else {
			this.job.paymentScheduleId = this.templateFromServer;
		}
		if (!this.quoteTotal) {
			this.quoteTotal = 0;
		}

		if (this.paymentScheduleTemplateId !== 0 || needToSendPayments) {
			this.payments.map(payment => delete payment.date);

			const paymentsToCalculate = this.generatePayments(
				paymentSchedule,
				needToSendPayments
			);

			this.paymentsDataService
				.calculatePaymentSchedule(paymentsToCalculate)
				.subscribe(data => {
					this.paymentsToShow = [];
					this.paymentsToShow = this.formPaymentsToShow(data.payments);
					this.emitPaymentsChanges();
				});
		} else {
			this.paymentsToShow = [];
			this.paymentsToShow = this.formPaymentsToShow(this.payments);

			this.emitPaymentsChanges();
		}
	}

	getTemplateId() {
		switch (+this.paymentScheduleTemplateId) {
			case 0:
				return this.templateFromServer;
			case -1:
				return null;
			default:
				return this.paymentScheduleTemplateId;
		}
	}

	generatePayments(paymentSchedule, needToSendPayments: boolean) {
		const paymentScheduleToReturn = this.getTemplateId();
		return {
			paymentScheduleId: paymentScheduleToReturn,
			jobDate: this.job.startDate,
			invoiceIssueDate: this.quoteUpdateService.getDate(this.issueDate.jsdate),
			totalAmount: this.quoteTotal,
			currencySymbol: this.currencySymbol,
			payments: needToSendPayments ? this.payments : []
		};
	}

	private formPaymentsToShow(payments) {
		return payments.map(payment =>
			Object.assign(payment, {
				date:
					payment.paymentDate === null
						? payment.paymentDate
						: this.quoteUpdateService.setQuoteIssueDate(payment.paymentDate),
				schedule: `${payment.paymentSchedulePart.amount}${this.setAmountType(
					payment.paymentSchedulePart.type
				)}`
			})
		);
	}

	setAmountType(type): string {
		switch (type) {
			case 'Percent of order':
				return '% of order';
			case 'Percent of remaining balance':
				return '% of remaining balance';
			default:
				return '';
		}
	}

	changePayments(date, index): void {
		this.payments[index].paymentDate =
			date === null ? null : this.quoteUpdateService.getDate(date.jsdate);
		this.payments[index].due =
			date === null ? null : this.quoteUpdateService.getDate(date.jsdate);
		const paymentScheduleIdToGet =
			this.paymentScheduleTemplateId === 0
				? this.paymentScheduleIdFromServer
				: this.paymentScheduleTemplateId;

		this.shouldGetDefaultPayments = false;

		this.getPaymentScheduleNew(paymentScheduleIdToGet, true, this.quoteTotal);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=nfw9yna8xro!./web/modern/components/quotes/common/payment-schedule/payment-schedule.ts